/**
 * Invoice amount validation utilities
 */

export type AmountValidationStatus = 'valid' | 'invalid' | 'unknown';

export interface AmountValidationInput {
  lineItems?: { quantity?: number; unitPrice?: number; amount?: number }[];
  subtotal?: number;
  taxes?: { amount: number }[];
  total?: number;
}

export const AMOUNT_TOLERANCE = 0.01;

/**
 * Round a number to a specified number of decimal places
 */
export function roundToPrecision(value: number, precision: number): number {
  const multiplier = Math.pow(10, precision);
  return Math.round(value * multiplier) / multiplier;
}

/**
 * Calculate the minimum precision needed for a calculated value to roundtrip correctly.
 *
 * For example, if qty=3, amount=1:
 * - unitPrice = 1/3 = 0.333...
 * - At precision 3: 0.333 × 3 = 0.999 (doesn't match 1.00)
 * - At precision 4: 0.3333 × 3 = 0.9999 (doesn't match 1.00)
 * - Need to find precision where qty × roundedUnitPrice ≈ amount (within tolerance)
 *
 * @param dividendValue - The value being divided (e.g., amount)
 * @param divisorValue - The value dividing by (e.g., quantity)
 * @param minPrecision - Minimum precision to use (display precision)
 * @param maxPrecision - Maximum precision to try (default 10)
 * @returns The precision needed, or maxPrecision if no exact match found
 */
export function calculateRoundtripPrecision(
  dividendValue: number,
  divisorValue: number,
  minPrecision: number,
  maxPrecision: number = 10
): number {
  if (divisorValue === 0) return minPrecision;

  const rawResult = dividendValue / divisorValue;

  // Try increasing precision until roundtrip works within tolerance
  for (let precision = minPrecision; precision <= maxPrecision; precision++) {
    const rounded = roundToPrecision(rawResult, precision);
    const roundtrip = rounded * divisorValue;

    if (Math.abs(roundtrip - dividendValue) < AMOUNT_TOLERANCE) {
      return precision;
    }
  }

  // If no exact match found, return max precision
  return maxPrecision;
}

/**
 * Calculate a value that will roundtrip correctly when multiplied back.
 * Returns the value rounded to the minimum precision needed for roundtripping.
 *
 * @param dividendValue - The value being divided (e.g., amount)
 * @param divisorValue - The value dividing by (e.g., quantity)
 * @param minPrecision - Minimum precision to use (display precision)
 * @returns The calculated value rounded to roundtrip-safe precision
 */
export function calculateWithRoundtrip(
  dividendValue: number,
  divisorValue: number,
  minPrecision: number
): number {
  if (divisorValue === 0) return 0;

  const precision = calculateRoundtripPrecision(dividendValue, divisorValue, minPrecision);
  const rawResult = dividendValue / divisorValue;

  return roundToPrecision(rawResult, precision);
}

/**
 * Check if two amounts match when rounded to 2 decimal places.
 * This is the precision used for monetary amounts.
 * Uses a tiny epsilon (0.0001) for floating-point comparison after rounding.
 */
export function amountsMatch(a: number | undefined | null, b: number): boolean {
  if (a == null) return false;
  const roundedA = roundToPrecision(a, 2);
  const roundedB = roundToPrecision(b, 2);
  return Math.abs(roundedA - roundedB) < 0.0001;
}

/**
 * Validate invoice amounts for arithmetic consistency:
 * - Line items: qty × unitPrice = amount
 * - Subtotal: sum of line item amounts
 * - Total: subtotal + taxes
 */
export function validateAmounts(data: AmountValidationInput): AmountValidationStatus {
  // If no line items or total, can't validate
  if (!data.lineItems?.length || data.total == null) {
    return 'unknown';
  }

  // Check line items: qty × unitPrice = amount
  for (const item of data.lineItems) {
    if (item.quantity != null && item.unitPrice != null && item.amount != null) {
      const expected = item.quantity * item.unitPrice;
      if (!amountsMatch(item.amount, expected)) {
        return 'invalid';
      }
    }
  }

  // Calculate subtotal from line items
  const calculatedSubtotal = data.lineItems.reduce((sum, item) => sum + (item.amount || 0), 0);

  // Check subtotal if present
  if (data.subtotal != null && !amountsMatch(data.subtotal, calculatedSubtotal)) {
    return 'invalid';
  }

  // Check total = subtotal + taxes
  const effectiveSubtotal = data.subtotal ?? calculatedSubtotal;
  const taxSum = data.taxes?.reduce((sum, t) => sum + t.amount, 0) || 0;
  const calculatedTotal = effectiveSubtotal + taxSum;

  if (!amountsMatch(data.total, calculatedTotal)) {
    return 'invalid';
  }

  return 'valid';
}
